import React, { useState, useEffect } from 'react';
import CollapsibleDayEvent from './CollapsibleDayEvent';

/* CALENDAR COMPONENT - Main calendar view used in CalendarPage */
/* Displays monthly calendar grid with navigation */
/* Shows event indicators on specific dates */
function Calendar({ events, onEditEvent, onDeleteEvent, onDateSelect, getEventsForDate }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [monthEvents, setMonthEvents] = useState([]);

  // Update events for the current month including recurring events
  useEffect(() => {
    if (events && events.length > 0 && getEventsForDate) {
      // Get all events for the month including recurring events
      const allEventsForMonth = [];

      // Get the month start and end dates
      const monthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      const monthEnd = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);

      // Loop through each day in the month
      const currentDate = new Date(monthStart);
      while (currentDate <= monthEnd) {
        // Get events for this day (including recurring ones)
        const eventsForDay = getEventsForDate(currentDate, events);

        // Add unique events (avoid duplicates from the same recurring event)
        eventsForDay.forEach(event => {
          // Check if we already have this event for this specific date
          const existingEventIndex = allEventsForMonth.findIndex(e =>
            e.id === event.id &&
            e.day === event.day &&
            e.month === event.month &&
            e.year === event.year
          );

          if (existingEventIndex === -1) {
            allEventsForMonth.push(event);
          }
        });

        // Move to next day
        currentDate.setDate(currentDate.getDate() + 1);
      }

      setMonthEvents(allEventsForMonth);
    } else {
      setMonthEvents([]);
    }
  }, [currentMonth, events, getEventsForDate]);

  /* CALENDAR HEADER - Renders month/year title with navigation arrows */
  /* Allows users to navigate between months */
  const header = () => {
    const dateFormat = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' });
    return (
      <div className="calendar-header" style={{
          marginBottom: '10px' /* Add bottom margin to header */
        }}>
        <div className="calendar-navigation" style={{
          paddingLeft: '6px', 
          paddingRight: '6px',
          display: 'flex',
          justifyContent: 'center', 
          alignItems: 'center', 
          position: 'relative',
          paddingTop: '6px', /* Add top padding to navigation */
          paddingBottom: '6px' /* Add bottom padding to navigation */
        }}>
          <h2 style={{margin: '0 auto', textAlign: 'center'}}>{dateFormat.format(currentMonth)}</h2>
          <div style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 'auto', 
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '200px'
          }}>
            <button 
              onClick={prevMonth} 
              style={{
                background: 'transparent',
                border: 'none',
                padding: '0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '30px',
                height: '30px'
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </button>
            
            <button 
              onClick={nextMonth}
              style={{
                background: 'transparent',
                border: 'none',
                padding: '0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '30px',
                height: '30px'
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>
          </div>
        </div>
      </div>
    );
  };

  /* DAYS OF WEEK - Renders the weekday labels (Sun-Sat) */
  /* Displayed at the top of the calendar grid */
  const daysOfWeek = () => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return (
      <div className="days-of-week">
        {days.map(day => (
          <div className="day-name" key={day}>
            {day}
          </div>
        ))}
      </div>
    );
  };

  /* CALENDAR CELLS - Renders the actual calendar grid */
  /* Creates day cells with event indicators */
  /* Highlights today's date and selected date */
  const cells = () => {
    const monthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const monthEnd = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
    const startDate = new Date(monthStart);
    const endDate = new Date(monthEnd);
    
    // Adjust start date to begin with the first day of the week (Sunday)
    startDate.setDate(1 - startDate.getDay());
    
    // Adjust end date to include days to complete the last week
    const lastDayOfWeek = endDate.getDay();
    if (lastDayOfWeek < 6) {
      endDate.setDate(endDate.getDate() + (6 - lastDayOfWeek));
    }
    
    const rows = [];
    let days = [];
    let day = new Date(startDate);
    
    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const cloneDay = new Date(day);
        const formattedDate = day.getDate();
        const isCurrentMonth = day.getMonth() === currentMonth.getMonth();
        const isToday = day.getDate() === new Date().getDate() && 
                        day.getMonth() === new Date().getMonth() && 
                        day.getFullYear() === new Date().getFullYear();
        const isSelected = day.getDate() === selectedDate.getDate() && 
                          day.getMonth() === selectedDate.getMonth() && 
                          day.getFullYear() === selectedDate.getFullYear();
        
        // Find events for this day (including recurring events)
        const dayEvents = monthEvents.filter(event =>
          event.day === formattedDate &&
          event.month === day.getMonth() + 1 &&
          event.year === day.getFullYear()
        );
        
        days.push(
          <div
            className={`day-cell ${!isCurrentMonth ? 'disabled' : ''} ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''} ${dayEvents.length > 0 ? 'has-events' : ''}`}
            key={day.toString()}
            onClick={() => handleDateClick(cloneDay)}
          >
            <div className="day-number">{formattedDate}</div>
            {/* Event indicators */}
            {dayEvents.length > 0 && (
              <div className="day-events">
                {dayEvents.slice(0, 3).map((event, eventIndex) => (
                  <div
                    key={eventIndex}
                    className="calendar-event"
                    style={{
                      backgroundColor: getCategoryColor(event.category),
                      color: 'white'
                    }}
                  >
                    {event.eventName}
                  </div>
                ))}
                {dayEvents.length > 3 && (
                  <div className="more-events" style={{ color: '#666' }}>
                    +{dayEvents.length - 3} more
                  </div>
                )}
              </div>
            )}
          </div>
        );
        day.setDate(day.getDate() + 1);
      }
      rows.push(
        <div className="calendar-row" key={day.toString()}>
          {days}
        </div>
      );
      days = [];
    }
    return <div className="calendar-body" style={{ paddingBottom: '15px' }}>{rows}</div>;
  };

  /* CATEGORY COLORS - Returns color codes for event categories */
  /* work=blue, school=orange, sport=red, life=green */
  const getCategoryColor = (category) => {
    switch(category) {
      case 'work': return '#1c83c9'; // Blue
      case 'school': return '#d78329'; // Orange
      case 'sport': return '#d34545'; // Red
      case 'life': return '#00994d'; // Green
      default: return '#4361ee'; // Default blue
    }
  };

  const getCategoryTextColor = (category) => {
    switch(category) {
      case 'work': return '#1c83c9';  // Blue
      case 'school': return '#d78329'; // Orange
      case 'sport': return '#d34545';  // Red
      case 'life': return '#00994d';   // Green
      default: return '#666666';
    }
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleDateClick = (day) => {
    setSelectedDate(day);
    if (onDateSelect) {
      onDateSelect(day);
    }
  };

  const isSameDay = (date1, date2) => {
    return date1.getDate() === date2.getDate() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getFullYear() === date2.getFullYear();
  };

  /* EVENT POPUP - Currently disabled (returns null) */
  /* Previously showed event details when clicking a date */
  const renderDayDetail = () => {
    // Return null to remove the events popup
    return null;
  };

  /* CALENDAR CONTAINER - Main wrapper for the entire calendar component */
  /* Handles responsive layout for mobile vs desktop */
  return (
    <div className="calendar-container" style={{ 
        position: 'relative', 
        overflow: 'visible',
        width: '100%',
        maxWidth: '100%',
        margin: '0 auto',
        boxSizing: 'border-box',
        padding: window.innerWidth <= 768 ? '0' : '0 16px', /* No padding on mobile */
        paddingTop: '16px', /* Add top padding for both mobile and desktop */
        marginTop: '10px'  /* Add margin to create more space at the top */
      }}>
      {header()}
      <div className="calendar-grid" style={{ 
        width: window.innerWidth <= 768 ? '100%' : '90%', /* Full width on mobile, narrower on desktop */
        margin: '0 auto', /* Center it */
        boxSizing: 'border-box',
        paddingTop: '12px' /* Add padding between header and calendar grid */
      }}>
        {daysOfWeek()}
        {cells()}
      </div>
      {renderDayDetail()}
    </div>
  );
}

export default Calendar;